/* ══ v9.0: Schedule — the read-only Gantt over the WBS ══════════════════════
   Everything here is DERIVED at render: bar geometry from the two optional
   per-element fields plannedStart/plannedFinish (tolerant reads — an invalid
   value renders as unset and is NEVER touched in the data, the v8.7 rule),
   the Q-a envelope from children, the fill from the v8.7 subtree roll-up.
   There is NO write path in this block — dates are edited on the
   Work-breakdown cards only (one writable home, the v8.8 dots precedent). */
/* v10.3 (W4): sanity-bound the planned-date YEAR as well as the shape.
   The Gantt derives its axis from these values and draws one gridline span per month per
   row with no clamp, so a single fat-fingered year — 2260 instead of 2026, one keystroke
   in a date input — produced ~2,800 months × every row ≈ 112,000 spans and HUNG the tab.
   And because renderSchedule is reached from refreshAll, the hang then recurred on every
   later edit. A programme plan lives within a few years of today; anything outside this
   window is a typo, so it is ignored for RENDERING (the stored value is left untouched —
   never silently rewrite the user's data — and the row shows as undated). */
const _PLAN_YEAR_MIN = 1990, _PLAN_YEAR_MAX = 2200;
function _planDateOk(v) {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const y = Number(v.slice(0, 4));
  return y >= _PLAN_YEAR_MIN && y <= _PLAN_YEAR_MAX;
}
function _wsPlanned(w) {
  const s = (w && _planDateOk(w.plannedStart)) ? w.plannedStart : null;
  const f = (w && _planDateOk(w.plannedFinish)) ? w.plannedFinish : null;
  return { s, f };
}
function _wsEnvelope(w, idx) {
  /* Q-a: a parent WITHOUT its own dates gets min(descendants' starts) →
     max(descendants' finishes) — display-only, never stored. A single-bounded
     descendant still stretches its side of the envelope. */
  let lo = null, hi = null;
  _wsSubtree(w.id, idx).forEach(x => {
    if (x.id === w.id) return;
    const p = _wsPlanned(x);
    const s = p.s || p.f, f = p.f || p.s;
    if (s && (!lo || s < lo)) lo = s;
    if (f && (!hi || f > hi)) hi = f;
  });
  return (lo && hi) ? { s: lo, f: hi } : null;
}
function renderSchedule() {
  const wrap = document.getElementById('ganttWrap');
  const emptyEl = document.getElementById('ganttEmpty');
  if (!wrap) return;
  const idx = _wsTreeIndex();
  const rows = [];
  const emit = (w) => { rows.push(w); (idx.children.get(w.id) || []).forEach(emit); };
  idx.roots.forEach(emit);

  /* per-row bar model: own dates → solid bar · date-less parent with dated
     descendants → dashed envelope (Q-a) · one date only → honest note ·
     nothing anywhere → barless grey note */
  let dated = 0;
  const model = rows.map(w => {
    const p = _wsPlanned(w);
    if (p.s && p.f) { dated += 1; return { w, kind: 'bar', s: p.s, f: p.f }; }
    if (p.s || p.f) { dated += 1; return { w, kind: 'partial', s: p.s, f: p.f }; }
    const env = _wsEnvelope(w, idx);
    if (env) return { w, kind: 'env', s: env.s, f: env.f };
    return { w, kind: 'none' };
  });
  const cEl = document.getElementById('scheduleCount');
  if (cEl) cEl.textContent = rows.length ? `${dated} / ${rows.length} dated` : '';

  const datedAtAll = model.some(m => m.kind !== 'none');
  if (!rows.length || !datedAtAll) {
    wrap.style.display = 'none'; wrap.innerHTML = '';
    /* elements but no dates → guidance; no elements at all → the WBS empty state already speaks */
    if (emptyEl) emptyEl.style.display = rows.length ? '' : 'none';
    return;
  }
  if (emptyEl) emptyEl.style.display = 'none';
  wrap.style.display = '';

  /* time range: min → max over every bar/envelope/partial bound, plus today,
     padded to whole months (exclusive end = first of the month after) */
  const today = todayISO();
  let minIso = today, maxIso = today;
  model.forEach(m => {
    if (m.kind === 'none') return;
    const s = m.s || m.f, f = m.f || m.s;
    if (s && s < minIso) minIso = s;
    if (f && f > maxIso) maxIso = f;
  });
  const lo = new Date(minIso + 'T00:00:00'); lo.setDate(1);
  const hi = new Date(maxIso + 'T00:00:00'); hi.setDate(1); hi.setMonth(hi.getMonth() + 1);
  const span = hi - lo;
  const pos = (isoStr) => Math.max(0, Math.min(100, ((new Date(isoStr + 'T00:00:00') - lo) / span) * 100));

  /* month gridlines + labels (label the year at January and on the first cell).
     v10.3 (W4): HARD CAP on the loop. _planDateOk already rejects absurd years, but a
     legitimately-allowed span (1990 → 2200) would still mint thousands of gridlines, and
     this markup is embedded per ROW below — so the cost is months × rows. Past the cap we
     stop drawing gridlines; the bars, the axis and the today marker are all positioned by
     percentage and stay correct, so an extreme range degrades to "no gridlines" instead of
     hanging the tab. */
  const _MAX_MONTH_LINES = 420;   /* 35 years of monthly gridlines */
  const months = [];
  for (let d = new Date(lo); d < hi && months.length < _MAX_MONTH_LINES; d.setMonth(d.getMonth() + 1)) {
    const isoStr = _isoLocal(d);   /* v10.3 (W4): was UTC — the month gridlines sat a day early */
    const lbl = d.toLocaleString('en-GB', { month: 'short' })
      + ((d.getMonth() === 0 || !months.length) ? ' ' + d.getFullYear() : '');
    months.push({ left: pos(isoStr), lbl });
  }
  const gridHtml = months.map(m => `<span class="gantt-grid" style="left:${m.left}%"></span>`).join('');
  const todayLeft = pos(today);
  const todayHtml = (todayLeft > 0 && todayLeft < 100)
    ? `<span class="gantt-today" title="Today — ${_escapeHtml(formatDateDisplay(today))}" style="left:${todayLeft}%"></span>` : '';

  const rowHtml = model.map(m => {
    const w = m.w;
    const depth = Math.min(idx.depth.get(w.id) || 1, 3);
    const code = idx.code.get(w.id) || '';
    const title = (w.title || '(untitled)');
    const pct = _projectProgress(w);
    let track;
    if (m.kind === 'bar' || m.kind === 'env') {
      const left = pos(m.s);
      const width = Math.max(0.8, pos(m.f) - left);
      const tip = `${code} ${title} — ${formatDateDisplay(m.s)} → ${formatDateDisplay(m.f)}`
        + (pct === null ? '' : ` · ${pct}% (actions)`)
        + (m.kind === 'env' ? ' · derived from sub-items' : '');
      const fill = (m.kind === 'bar' && pct !== null) ? `<i class="gantt-fill" style="width:${pct}%"></i>` : '';
      track = `<span class="gantt-bar${m.kind === 'env' ? ' gantt-env' : ''}" style="left:${left}%;width:${width}%" title="${_escapeHtml(tip)}">${fill}</span>`;
    } else if (m.kind === 'partial') {
      track = `<span class="gantt-note">${m.s ? 'starts ' + _escapeHtml(formatDateDisplay(m.s)) + ' · finish not set' : 'finishes ' + _escapeHtml(formatDateDisplay(m.f)) + ' · start not set'}</span>`;
    } else {
      track = `<span class="gantt-note">no dates</span>`;
    }
    return `<div class="gantt-row gantt-lvl${depth}" data-id="${w.id}">`
      + `<div class="gantt-label"><span class="wbs-code">${_escapeHtml(code)}</span><span class="gantt-title" title="${_escapeHtml(title)}">${_escapeHtml(title)}</span></div>`
      + `<div class="gantt-track">${gridHtml}${todayHtml}${track}</div>`
      + `</div>`;
  }).join('');

  wrap.innerHTML = `<div class="gantt-inner">`
    + `<div class="gantt-axis-row"><div class="gantt-axis-lbl">Element</div><div class="gantt-axis">`
    + months.map(m => `<span class="gantt-month" style="left:${m.left}%">${_escapeHtml(m.lbl)}</span>`).join('')
    + `</div></div>` + rowHtml + `</div>`;
}

/* ══ v9.0: actions ↔ WBS traceability ═══════════════════════════════════════
   The element pill on the Actions tab's LIST cards (kanban + Focus deliberately
   excluded — recorded candidates). Membership is DERIVED from
   workstream.actionIds (one-project-per-action) — there is NO action.workstreamId
   key. Pills appear only once a breakdown exists (the coverage-strip philosophy:
   an empty WBS shouldn't nag on every card). */
function _wbsPathLabel(w, idx) {
  const ix = idx || _wsTreeIndex();
  const parts = []; const guard = new Set();
  let cur = w;
  while (cur && !guard.has(cur.id)) {
    guard.add(cur.id);
    parts.unshift(((ix.code.get(cur.id) || '') + ' ' + (cur.title || '(untitled)')).trim());
    const pid = ix.parent.get(cur.id);
    cur = (pid == null) ? null : _wsById(pid);
  }
  return parts.join(' › ');
}
function _wbsPillHtml(actionId) {
  /* ⚠ v16.5 (a3): THE EMPTY-BREAKDOWN RULE NOW EXEMPTS AN EXPLICIT MARKING. The original
     rule - an empty WBS should not nag on every card - was written for a breakdown that NEVER
     EXISTED. It also fired for one that had just been EMPTIED, so a "not a WBS item" the user
     deliberately set vanished from the screen while staying in the data. ⚠ A state the user
     set must not become invisible - that is exactly why this pill is clickable (v12.2 ◆D:
     "an invisible state cannot be undone"). The nag half is unchanged: an UNMARKED action
     still shows nothing at all while there is no breakdown. */
  if (!WORKSTREAMS.length && !_actionIsNoWbs(actionId)) return '';
  const w = _projectOwningAction(actionId);
  if (!w) {
    /* v12.2 (W3, ◆D): a marked action shows a quiet n/a that is STILL clickable, because an
       invisible state cannot be undone. An assigned action never reaches here, so an
       assignment always wins over the flag. */
    if (_actionIsNoWbs(actionId)) {
      return `<span class="wbs-pill wbs-pill-na" title="Marked as not a WBS item — click to change" onclick="openWbsAssign(${actionId});event.stopPropagation()">n/a</span>`;
    }
    return `<span class="wbs-pill wbs-pill-none" title="Not assigned to any breakdown element — click to give it a home" onclick="openWbsAssign(${actionId});event.stopPropagation()">unassigned</span>`;
  }
  const idx = _wsTreeIndex();
  const code = idx.code.get(w.id) || '';
  const t = (w.title || '(untitled)');
  const short = t.length > 24 ? t.slice(0, 24) + '…' : t;
  return `<span class="wbs-pill" title="${_escapeHtml(_wbsPathLabel(w, idx))} — click to open in the Work breakdown" onclick="gotoWbsElement(${w.id});event.stopPropagation()">${_escapeHtml(code)} · ${_escapeHtml(short)}</span>`;
}
function _flashJumpTarget(el) {
  el.classList.remove('wbs-jump-flash');
  void el.offsetWidth;   /* restart the animation if re-triggered */
  el.classList.add('wbs-jump-flash');
  setTimeout(() => el.classList.remove('wbs-jump-flash'), 1700);
}
function gotoWbsElement(wsId) {
  switchTab('scope');
  const sec = document.getElementById('workstreamsSection');
  if (sec) sec.open = true;
  const card = document.querySelector(`#spList .ws-card[data-id="${wsId}"]`);
  if (!card) return;
  card.classList.add('expanded');
  _wsSetExpanded(wsId, true);
  card.scrollIntoView({ block: 'center', behavior: 'smooth' });
  _flashJumpTarget(card);
}
/* ══ v14.2 (d): WHICH FILTER IS HIDING IT - asked, then said out loud ═══════════════════
   ⚠ HIS RULING: "dont clear filters, it would confuse users." Then the better question,
   "which use case would lead to an empty display?", which narrowed the problem and showed my
   own "clear the filters" recommendation was wrong.
   getBoardFiltered() NEUTRALISES exactly two filters (activeStatus, hideCompleted) because the
   board's columns ARE statuses, and body.actions-board CSS HIDES both controls - so they are
   invisible, ignored by the board, and still set. Those two, and ONLY those two, can strand a
   jump: a card visible on the board has already passed priority and search, so clearing those
   would be the pointless, confusing act he objected to.
   ⚠ So: land as normal, and if the card is not there, NAME the filter and offer a button. The
   filter stays his. */
function _whyActionIsHidden(actionId, allFilters) {
  const action = getAllActions().find(a => String(a.id) === String(actionId));
  const e = edits[actionId];
  if (!e || !action) return null;
  /* ⚠ v15.2: mirror getFiltered's OWN precedence, or the diagnosis lies. getFiltered
     suspends hide-closed while the status filter itself asks for a closed status — so
     blaming hide-closed then ("your Cancelled card is hidden because closed actions are
     hidden") named a filter that was not even in effect, and its fix-button visibly did
     nothing. The status filter is also checked FIRST for the same reason: when both apply,
     the status filter is the one whose fix can actually reveal the card on its own. */
  if (activeStatus !== 'all' && statusKey(e.status) !== activeStatus) return 'status';
  const hideClosedInEffect = hideCompleted
    && !(activeStatus === 'completed' || activeStatus === 'cancelled');
  if (hideClosedInEffect && isClosedStatus(e.status)) return 'hideCompleted';
  /* Board-origin jumps can only be stranded by the two filters above.  Overview-origin
     jumps start outside Actions, so diagnose the remaining visible controls too. */
  if (allFilters) {
    if (activePriority !== 'all' && String(e.priority || '').toLowerCase() !== activePriority)
      return 'priority';
    if (activeStatFilter && activeStatFilter !== 'all') return 'summary';
    if (searchQuery) return 'search';
  }
  return null;
}

/* ⚠ THE LANDING DIFFERS BY WHERE YOU CAME FROM, and that is not an inconsistency - it is the
   scope's own table. FROM THE BOARD a card you can see has ALREADY passed priority and search
   (getBoardFiltered only neutralises activeStatus and hideCompleted), so those two are the only
   filters that can strand the jump, and he ruled they must be NAMED rather than cleared:
   "dont clear filters, it would confuse users."
   FROM ANYWHERE ELSE - a WBS action row, a stat pill - any filter can be hiding the target,
   including a search you typed on another tab, and clearing it is the established v10.x
   behaviour with a check on it (pill_clickthroughs). Telling instead of clearing there would
   be a regression, not a fix; I tried it and the battery said so.
   ⚠ Default keeps the old behaviour, so every existing caller is untouched. */
function gotoActionCard(actionId, opts) {
  switchTab('actions');
  /* ⚠ v15.2: from a WBS action row or a stat pill with the BOARD up, the card exists but
     sits in the display:none LIST — scrolling and flashing it was a perfect no-op, and a
     sub-action target is not even a board card. v14.2 (d) reset the view for the
     board-click jump; every other caller needed the same reset and never got it. */
  if (typeof actionsView !== 'undefined' && actionsView === 'board') setActionsView('list');
  let card = document.getElementById('card-' + actionId);
  if (!card) {
    if (opts && opts.tellDontClear) {
      const why = _whyActionIsHidden(actionId, !!opts.tellAllFilters);
      const label = displayActionId(actionId);
      if (why === 'hideCompleted') {
        _hiddenJumpToast(actionId, label + ' is hidden because closed actions are hidden',
                         toggleHideCompleted);         /* the app's own setter, so the button
                                                          label and state move with it */
      } else if (why === 'status') {
        /* v15.2: the filter's DISPLAY label, not its internal kebab key \u2014 the toast read
           \u201chidden by your \u201cin-progress\u201d status filter\u201d, words no control on the page shows. */
        const statusLabel = String(activeStatus).replace(/-/g, ' ')
          .replace(/^\w/, ch => ch.toUpperCase());
        _hiddenJumpToast(actionId, label + ' is hidden by your \u201c' + statusLabel + '\u201d status filter',
                         () => {
                           activeStatus = 'all';
                           document.querySelectorAll('#statusFilters .filter-btn').forEach(b =>
                             b.classList.toggle('active', b.dataset.filter === 'all'));
                           refreshAll();
                         });
      } else if (why === 'priority') {
        const priorityLabel = String(activePriority).replace(/^\w/, ch => ch.toUpperCase());
        _hiddenJumpToast(actionId, label + ' is hidden by your “' + priorityLabel + '” priority filter',
                         () => {
                           activePriority = 'all'; activeStatFilter = 'all';
                           document.querySelectorAll('#priorityFilters .filter-btn').forEach(b =>
                             b.classList.toggle('active', b.dataset.priority === 'all'));
                           refreshAll();
                         });
      } else if (why === 'summary') {
        _hiddenJumpToast(actionId, label + ' is hidden by your “' +
                         String(activeStatFilter).replace(/-/g, ' ') + '” summary filter',
                         () => applyStatFilter('all'));
      } else if (why === 'search') {
        _hiddenJumpToast(actionId, label + ' is hidden by your action search', clearSearch);
      } else {
        /* neither of the two the board neutralises - say so honestly rather than guess */
        _hiddenJumpToast(actionId, label + ' is hidden by your current filters',
                         clearAllActionFilters);
      }
      return;
    }
    clearAllActionFilters();            /* every other caller: unchanged since v10.x */
    card = document.getElementById('card-' + actionId);
  }
  _landOnActionCard(card);
}

/* The jump itself, shared by the board click and every other caller. */
function _landOnActionCard(card) {
  if (!card) return;
  if (!card.classList.contains('expanded')) {
    card.classList.add('expanded');
    if (typeof syncExpandToggleLabels === 'function') syncExpandToggleLabels();
  }
  card.scrollIntoView({ block: 'center', behavior: 'smooth' });
  _flashJumpTarget(card);
}

/* ⚠ SAME LOOK AS THE UNDO TOAST, DELIBERATELY NOT THE SAME MACHINERY. The scope said to reuse
   the toast-with-an-action idiom, and visually this does - identical classes, identical host.
   But showUndoToast() is welded to the ONE shared undo slot: it calls clearPendingUndo() on
   entry, which COMMITS whatever delete is pending, and its button ignores the function you pass
   and fires pendingUndo.restore instead. Routing a filter hint through it would silently commit
   a delete the user could still have undone. So this borrows the CSS and owns its lifecycle.
   "Show it" is the only thing that changes a filter, and he presses it. */
function _hiddenJumpToast(actionId, message, showIt) {
  let host = document.getElementById('toastHostBottomLeft');
  if (!host) {
    host = document.createElement('div');
    host.id = 'toastHostBottomLeft';
    document.body.appendChild(host);
  }
  const el = document.createElement('div');
  el.className = 'toast toast-undo';
  const span = document.createElement('span');
  span.textContent = message;
  el.appendChild(span);
  const btn = document.createElement('button');
  btn.className = 'toast-undo-btn';
  btn.textContent = 'Show it';
  const close = () => { el.classList.remove('toast-visible');
                        setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 200); };
  btn.onclick = () => {
    close();
    showIt();
    /* ⚠ v15.2: VERIFY THE LANDING. Two filters can hide one card; fixing the named one and
       then landing on null read as the button doing nothing. If it is still hidden, diagnose
       again — tell, never silently clear (his ruling). */
    const c2 = document.getElementById('card-' + actionId);
    if (c2) { _landOnActionCard(c2); return; }
    if (typeof gotoActionCard === 'function') gotoActionCard(actionId, { tellDontClear: true });
  };
  el.appendChild(btn);
  host.appendChild(el);
  void el.offsetWidth;
  el.classList.add('toast-visible');
  setTimeout(close, 6000);
}

/* v14.2 (d): a click on a board card lands you on it in the list. A plain click there did
   NOTHING before, so no gesture is displaced. */
function jumpToActionFromBoard(actionId) {
  setActionsView('list');
  /* ⚠ v14.2 (d), found by LOOKING AT THE RENDERED THING and by nothing else: setActionsView
     only SHOWS the list container - it does not repaint it. The list therefore kept the paint
     it had while the board was up, and render() suppresses the "no actions match" empty state
     whenever actionsView is 'board'. So landing on a filtered-empty list left a completely
     blank page: no cards, no empty state, and once the toast timed out, no explanation either.
     Repainting for the view we have just switched to is what makes the landing honest. */
  render();
  gotoActionCard(actionId, { tellDontClear: true });   /* v14.2 (d): his ruling, board only */
}

/* ── Action-assignment picker modal ──────────────────────────────────────── */
let _pickerProjectId = null;
function openActionPicker(projectId) {
  _pickerProjectId = projectId;
  const p = WORKSTREAMS.find(x => x.id === projectId);
  if (!p) return;
  const sub = document.getElementById('spPickerSub');
  if (sub) sub.textContent = `to "${(p.title || 'Untitled project')}" · check to add, uncheck to remove`;
  const search = document.getElementById('spPickerSearch');
  if (search) search.value = '';
  /* v3.3 patch: sync the Open-only checkbox state from the persisted preference */
  const openOnlyEl = document.getElementById('spPickerOpenOnly');
  if (openOnlyEl) openOnlyEl.checked = !!appMeta.pickerOpenOnly;
  _renderPickerList('');
  const ov = document.getElementById('spPickerOverlay');
  if (ov) ov.classList.add('open');
  if (search) search.focus();
}
function closeActionPicker() {
  const ov = document.getElementById('spPickerOverlay');
  if (ov) ov.classList.remove('open');
  _pickerProjectId = null;
}
/* v3.3 patch: toggle the persisted "Open only" filter + re-render the picker. */
function togglePickerOpenOnly() {
  const el = document.getElementById('spPickerOpenOnly');
  if (!el) return;
  appMeta.pickerOpenOnly = !!el.checked;
  markDirty();
  const search = document.getElementById('spPickerSearch');
  _renderPickerList(search ? search.value : '');
}
function _renderPickerList(query) {
  const host = document.getElementById('spPickerList');
  if (!host) return;
  const p = WORKSTREAMS.find(x => x.id === _pickerProjectId);
  if (!p) { host.innerHTML = ''; return; }
  const q = (query || '').toLowerCase();
  const openOnly = !!appMeta.pickerOpenOnly;
  const linkedHere = new Set(Array.isArray(p.actionIds) ? p.actionIds : []);
  const actions = getAllActions().filter(a => {
    const e = edits[a.id] || {};
    /* v3.3 patch: "Open only" filter hides completed + cancelled — UNLESS the
       action is already linked to THIS project. That preserves the unlink path
       (an action might have been linked while still open and then completed). */
    if (openOnly && !linkedHere.has(a.id)) {
      const status = e.status || STATUS_DISPLAY[a.status] || a.status;
      if (status === 'Completed' || status === 'Cancelled') return false;
    }
    if (!q) return true;
    /* v14.2 (b): search the number he can SEE. Typing "4" must find the card labelled #4,
       or search silently disagrees with the screen - the same display-and-input rule as the
       two chip inputs, one field further along. */
    return (`${displayActionId(a.id)} ${(actField(e, a, 'desc') || '')}`).toLowerCase().includes(q);
  });
  host.innerHTML = '';
  if (!actions.length) {
    /* v3.3 patch: empty-state hints at the filter when it's on */
    if (openOnly) {
      host.innerHTML = '<div class="ws-modal-empty">No open actions match. Uncheck "Open only" to include completed/cancelled.</div>';
    } else {
      host.innerHTML = '<div class="ws-modal-empty">No actions match.</div>';
    }
    return;
  }
  actions.forEach(a => {
    const e = edits[a.id] || {};
    const title = (actField(e, a, 'desc') || '').trim() || '(untitled)';
    const owner = _projectOwningAction(a.id);
    const inThis = owner && owner.id === p.id;
    const inOther = owner && owner.id !== p.id;
    const row = document.createElement('div');
    row.className = 'ws-pick-row' + (inThis ? ' checked' : '') + (inOther ? ' disabled' : '');
    row.innerHTML =
      '<span class="ws-pick-check"><svg viewBox="0 0 12 12"><path d="M2 6l3 3 5-6"/></svg></span>' +
      `<span class="ws-pick-id">${displayActionId(a.id)}</span>` +
      `<span class="ws-pick-title">${_escapeHtml(title)}</span>` +
      (inOther ? `<span class="ws-pick-elsewhere">in: ${_escapeHtml(owner.title || 'Untitled project')}</span>` : '');
    if (!inOther) {
      row.onclick = () => _togglePickAction(a.id);
    }
    host.appendChild(row);
  });
}
function _togglePickAction(actionId) {
  const p = WORKSTREAMS.find(x => x.id === _pickerProjectId);
  if (!p) return;
  if (!Array.isArray(p.actionIds)) p.actionIds = [];
  const at = p.actionIds.indexOf(actionId);
  if (at >= 0) p.actionIds.splice(at, 1);
  else {
    /* one-project-per-action: only assignable if not owned by another project */
    if (_projectOwningAction(actionId)) return;
    p.actionIds.push(actionId);
    /* ⚠ v16.5 (a2): ASSIGNING CLEARS "not a WBS item" HERE TOO. ◆E at v12.2 ruled that the
       two states "must never contradict each other", and confirmWbsPick has cleared the flag
       ever since - but this path, the workstream card's OWN picker, never did. The flag then
       hid behind the assignment (an assignment always wins in _wbsPillHtml) and resurfaced as
       "n/a" the moment the workstream was deleted. ⚠ Worse than the label: renderWbsCoverage
       drops noWbs actions from the DENOMINATOR, so an assigned-and-flagged action left
       coverage able to read 100% while real work sat outside the count. */
    _setActionNoWbs(actionId, false);
  }
  markDirty();
  const search = document.getElementById('spPickerSearch');
  _renderPickerList(search ? search.value : '');
  _wbsRepaintMembership();   /* v16.5 (a1): the card behind the modal AND the action cards */
}

/* ── Boot wiring for all v3.2 dashboard sections ─────────────────────────── */
